import { useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { useWalletContext } from 'hooks/context/useWalletContext'
import { useSendTransactionsReportMutation } from 'hooks/mutations/useSendTransactionsReportMutation'
import { useSendAllTransactionsToEmailMutation } from 'hooks/mutations/useTransactionExportToEmailAllWalletsMutation'
import { useSendWalletTransactionsToEmailMutation } from 'hooks/mutations/useTransactionExportToEmailMutation'
import {
  customDateValueOptions,
  exportFormatRadioOptions,
} from 'pages/WalletDetails/constants/walletTransactionsExportOptions'
import { buildWalletExportPayload } from 'services/TransactionService'
import { BaseErrorResponse } from 'types/beneficiaries'
import { CustomerWallet } from 'types/customers'
import { ErrorCodes } from 'types/errorCodes'
import { WalletTransactionsExport } from 'types/transaction/GetTransactionsReq'
import {
  ExportFileFormat,
  ExportFileFormatType,
  ExportTabItems,
  OnceOffExportForm,
  OnceOffExportSteps,
  OnceOffExportStepsType,
  ReportTypes,
} from 'types/walletTransactionsExport'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface UseOnceOffExportHelperProps {
  handleClose: () => void
  downloadAll: boolean
  customerWallet?: CustomerWallet
}

export const useOnceOffExportHelper = ({
  downloadAll,
  customerWallet,
  handleClose,
}: UseOnceOffExportHelperProps) => {
  const { t } = useTranslation()
  const { enableReportingService } = useFeatureFlags()
  const wallet = useWalletContext()

  const {
    mutate: sendAllTransactionsToEmail,
    isPending: isLoadingAllTransactionsExport,
    error: allTransactionsExportError,
    isError: isErrorAllTransactionsExport,
  } = useSendAllTransactionsToEmailMutation()
  const {
    mutate: sendWalletTransactionsToEmail,
    isPending: isLoadingWalletTransactionsExport,
    error: walletTransactionsExportError,
    isError: isErrorWalletTransactionsExport,
  } = useSendWalletTransactionsToEmailMutation()

  const {
    mutate: sendTransactionsReport,
    isPending: isLoadingSendTransactionsReport,
    error: sendTransactionsReportError,
    isError: isErrorSendTransactionsReport,
  } = useSendTransactionsReportMutation()

  const [step, setStep] = useState<OnceOffExportStepsType>(OnceOffExportSteps.FORM)
  const [exportFormat, setExportFormat] = useState<ExportFileFormatType>(ExportFileFormat.CSV)
  const [dateValue, setDateValue] = useState<DateRange>({
    from: null,
    to: null,
  })

  const exportFormatOptions = exportFormatRadioOptions()
  const exportCustomDateOptions = customDateValueOptions(dateValue?.from, dateValue?.to)

  const { watch, setValue } = useForm<OnceOffExportForm>({
    defaultValues: {
      reportType: ReportTypes.TRANSACTION,
      selectedDateOptionName: exportCustomDateOptions?.last30Days?.name,
      selectedExportFormat: exportFormatOptions[0].value as ExportFileFormatType,
      balanceDate: undefined,
    },
    mode: 'onChange',
  })
  const formValues: OnceOffExportForm = watch()

  const customDateValueName = formValues.selectedDateOptionName
  const selectedFromDateVal = exportCustomDateOptions?.[customDateValueName]?.fromDate
  const selectedToDateVal = exportCustomDateOptions?.[customDateValueName]?.toDate
  const selectedDateRange = { from: selectedFromDateVal, to: selectedToDateVal }
  const walletId = customerWallet ? customerWallet?.id : wallet?.id
  const walletLSID = customerWallet ? customerWallet?.id : wallet?.lsid

  const amplitudeProperties = {
    category: AmplitudeEventCategory.MERCHANT_PORTAL,
    action: AmplitudeEventAction.LOAD,
    type: ExportTabItems.ONCE_OFF,
    format: formValues?.selectedExportFormat,
    from: selectedFromDateVal,
    to: selectedToDateVal,
    ...(!downloadAll && {
      walletId,
    }),
  }

  const handleSendTransactionsReport = () => {
    const walletExport = buildWalletExportPayload({
      reportType: formValues?.reportType,
      selectedExportFormat: formValues?.selectedExportFormat,
      walletLSID,
      selectedDateRange,
      balanceDate: formValues?.balanceDate,
    })

    sendTransactionsReport(walletExport, {
      onSuccess: () => {
        track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_SUCCESS, {
          ...amplitudeProperties,
        })

        toast({
          header: t('exports.checkYourMail'),
          variant: 'success',
          body: t('exports.checkYourMailDescription'),
        })
        handleClose()
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_ERROR, {
          ...amplitudeProperties,
          error,
        })

        if (error?.data?.code !== ErrorCodes.REPORT_LEDGER_DUPLICATE_REQUEST) {
          logSentryError(
            'Error from TransactionExportModal - handleTransactionExportToEmail',
            error,
            walletExport
          )
        }
      },
    })
  }

  const handleTransactionExportToEmail = () => {
    if (downloadAll) {
      if (enableReportingService) {
        handleSendTransactionsReport()
      } else {
        sendAllTransactionsToEmail(selectedDateRange, {
          onSuccess: () => {
            track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_SUCCESS, {
              ...amplitudeProperties,
            })

            toast({
              header: t('exports.checkYourMail'),
              variant: 'success',
              body: t('exports.checkYourMailDescription'),
            })
            handleClose()
          },
          onError: (error: BaseErrorResponse) => {
            track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_ERROR, {
              ...amplitudeProperties,
              error,
            })
            logSentryError(
              'Error from TransactionExportModal - handleTransactionExportToEmail',
              error
            )
          },
        })
      }
    } else {
      if (enableReportingService) {
        handleSendTransactionsReport()
      } else {
        const walletExport: WalletTransactionsExport = {
          selectedExportFormat: formValues.selectedExportFormat,
          walletId,
          selectedDateRange,
          isV2Enabled: !!customerWallet,
        }

        sendWalletTransactionsToEmail(walletExport, {
          onSuccess: () => {
            track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_SUCCESS, {
              ...amplitudeProperties,
            })

            toast({
              header: t('exports.checkYourMail'),
              variant: 'success',
              body: t('exports.checkYourMailDescription'),
            })
            handleClose()
          },
          onError: (error: BaseErrorResponse) => {
            track.Amp.track(AmplitudeEvent.EOF_CREATE_EXPORT_INITIATE_ERROR, {
              ...amplitudeProperties,
              error,
            })
            logSentryError(
              'Error from TransactionExportModal - handleTransactionExportToEmail',
              error,
              walletExport
            )
          },
        })
      }
    }
  }

  return {
    step,
    setStep,
    formValues,
    setValue,
    exportCustomDateOptions,
    dateValue,
    setDateValue,
    exportFormatOptions,
    exportFormat,
    setExportFormat,
    handleTransactionExportToEmail,
    isExporting:
      isLoadingWalletTransactionsExport ||
      isLoadingAllTransactionsExport ||
      isLoadingSendTransactionsReport,
    handleClose,
    allTransactionsExportError,
    isErrorAllTransactionsExport,
    walletTransactionsExportError,
    isErrorWalletTransactionsExport,
    sendTransactionsReportError,
    isErrorSendTransactionsReport,
  }
}
